import { loanApi } from "@api/loans";
import { patronApi } from "@api/patrons";
import { recordToPidType } from "@api/utils";
import { EditButton } from "@components/backoffice/buttons/EditButton";
import {
  ScrollingMenu,
  ScrollingMenuItem,
} from "@components/backoffice/buttons/ScrollingMenu";
import { DeleteRecordModal } from "@components/backoffice/DeleteRecordModal";
import { DeleteButton } from "@components/backoffice/DeleteRecordModal/DeleteButton";
import { LoanIcon } from "@components/backoffice/icons";
import { invenioConfig } from "@config";
import { goTo } from "@history";
import { ESSelectorModal } from "@modules/ESSelector";
import { serializePatron } from "@modules/ESSelector/serializer";
import { BackOfficeRoutes } from "@routes/urls";
import { http } from "@api/base";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Button, Divider, Message } from "semantic-ui-react";
import _first from "lodash/first";
import _get from "lodash/get";

export default class ItemActionMenu extends Component {
  constructor(props) {
    super(props);
    this.state = {
      checkOutOnEnter: false,
      errorMessage: null,
    };
  }

  checkOutDisabled() {
    const {
      item: { metadata },
    } = this.props;
    return (
      invenioConfig.CIRCULATION.loanActiveStates.includes(
        metadata.circulation.state
      ) || !invenioConfig.ITEMS.canCirculateStatuses.includes(metadata.status)
    );
  }

  hasCheckOutItem() {
    const {
      checkOutitemList,
      item: { metadata },
    } = this.props;
    const firstItem = _first(checkOutitemList);
    return (
      !this.checkOutDisabled() &&
      _get(firstItem, "metadata.barcode") === metadata.barcode
    );
  }

  handleOnRefClick(loanPid) {
    goTo(BackOfficeRoutes.loanDetailsFor(loanPid));
  }

  createRefProps(itemPid) {
    return [
      {
        refType: "Loan",
        onRefClick: this.handleOnRefClick,
        getRefData: () =>
          loanApi.list(
            loanApi
              .query()
              .withItemPid(itemPid)
              .withState(invenioConfig.CIRCULATION.loanActiveStates)
              .qs()
          ),
      },
    ];
  }

  checkoutItemButton = () => {
    return (
      <Button
        primary
        icon
        fluid
        labelPosition="left"
        size="small"
        disabled={this.checkOutDisabled()}
        style={{ textAlign: "right", marginRight: "25px" }}
      >
        <LoanIcon />
        Checkout This Copy
      </Button>
    );
  };

  deleteDocButton = (props) => {
    return (
      <DeleteButton
        fluid
        content="Delete Physical Copy"
        labelPosition="left"
        style={{ textAlign: "right", marginRight: "25px" }}
        {...props}
      />
    );
  };

  // Fetch patron profile data from backend
  fetchPatronProfile = async (patronId) => {
    try {
      const response = await http.get(`/api/patrons/${patronId}/profile`);
      return response.data;
    } catch (error) {
      return null;
    }
  };

  // Fetch program details from backend
  fetchProgramDetails = async (programName) => {
    try {
      const response = await http.get("/courses/programs");
      // Access the nested data array
      const programs = response.data?.data || [];

      if (!Array.isArray(programs)) {
        return "";
      }

      // Find matching program by exact name or partial match
      const program = programs.find(
        (p) => p.name && p.name.toLowerCase() === programName.toLowerCase()
      );

      // If exact match not found, try partial match
      const partialMatch =
        !program &&
        programs.find(
          (p) =>
            p.name && p.name.toLowerCase().includes(programName.toLowerCase())
        );

      const matchedProgram = program || partialMatch;

      return matchedProgram ? matchedProgram.code : "";
    } catch (error) {
      return "";
    }
  };

  checkoutItem = async (results, additionalInfo = {}) => {
    const {
      checkoutItem,
      item: { metadata },
      item,
    } = this.props;

    const selectedPatron = results[0];
    const { patronStatus, overrideActive } = additionalInfo;
    const patronMetadata = selectedPatron.metadata;

    // Determine checkout type based on patron tags
    let checkoutType = "direct_checkout";
    let requestInfo = null;

    if (patronMetadata.hasRequestForThisItem) {
      checkoutType = "fulfill_specific_request";
      requestInfo = {
        type: "specific_item",
        message: "Fulfilling existing request for this specific copy",
      };
    } else if (patronMetadata.hasRequestForOtherCopy) {
      checkoutType = "fulfill_alternate_request";
      requestInfo = {
        type: "alternate_item",
        message: "Using this copy to fulfill request for different copy",
      };
    } else {
      checkoutType = "direct_checkout";
      requestInfo = {
        type: "library_use",
        message: "Direct checkout for library use - same day return required",
      };
    }

    // Validation based on patron status
    if (patronStatus) {
      const { hasOverdueItems, borrowingStats } = patronStatus;
      const hasRestrictions = hasOverdueItems || !borrowingStats.canBorrow;

      if (hasRestrictions && !overrideActive) {
        let errorMessage = `Cannot Checkout: `;

        if (hasOverdueItems) {
          errorMessage += "Patron has overdue loans. ";
        }
        if (!borrowingStats.canBorrow) {
          errorMessage += `Patron has reached borrowing limit (${borrowingStats.current}/${borrowingStats.limit}). `;
        }
        errorMessage += "Please resolve restrictions first.";

        this.setState({
          errorMessage: <>{errorMessage}</>,
        });
        return false;
      }
    } else {
      // Fallback validation
      if (selectedPatron.metadata.hasOverdueLoans) {
        this.setState({
          errorMessage: (
            <>
              <strong>Cannot Checkout:</strong> Patron{" "}
              {selectedPatron.metadata.name || selectedPatron.metadata.pid} has
              overdue loans. Please resolve overdue items first.
            </>
          ),
        });
        return false;
      }
    }

    this.setState({ errorMessage: null });

    // Fetch patron profile data from backend
    const patronId = selectedPatron.metadata.id || selectedPatron.metadata.pid;
    const patronProfile = await this.fetchPatronProfile(patronId);

    // Extract borrower info from patron profile
    let borrowerInfo = {
      first_name: "",
      last_name: "",
      contact_no: "",
      college: "",
      program: "",
      program_code: "",
      year: "",
      semester: "",
    };

    if (patronProfile) {
      // Parse full name into first and last name
      const fullName = patronProfile.full_name || patronProfile.name || "";
      const nameParts = fullName.trim().split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      borrowerInfo = {
        first_name: firstName,
        last_name: lastName,
        contact_no: patronProfile.contact_number || patronProfile.email || "",
        college: patronProfile.department || "",
        program: patronProfile.program || "",
        program_code: patronProfile.program_code || "",
        year: patronProfile.year || "",
        semester: patronProfile.semester || "",
      };

      // If program_code is missing but we have program name, try to fetch it
      if (!borrowerInfo.program_code && borrowerInfo.program) {
        const programCode = await this.fetchProgramDetails(
          borrowerInfo.program
        );
        if (programCode) {
          borrowerInfo.program_code = programCode;
        }
      }
    } else {
      // Fallback to basic patron metadata if profile fetch fails
      const nameParts = (patronMetadata.name || "").trim().split(" ");
      borrowerInfo.first_name = nameParts[0] || "";
      borrowerInfo.last_name = nameParts.slice(1).join(" ") || "";
      borrowerInfo.contact_no = patronMetadata.email || "";
    }

    // Prepare checkout data
    const documentPid = metadata.document_pid;
    const itemPid = {
      type: recordToPidType(item),
      value: item.metadata.pid,
    };
    const patronPid = patronId;

    const today = new Date().toISOString().split("T")[0];

    // Enhanced checkout data with type information
    const checkoutData = {
      documentPid,
      itemPid,
      patronPid,
      checkoutType,
      requestInfo,
      hasExistingRequest:
        patronMetadata.hasRequestForThisItem ||
        patronMetadata.hasRequestForOtherCopy,
      requestPriority: patronMetadata.requestPriority || "normal",
      borrower_info: borrowerInfo,
    };

    // Set delivery method and dates based on checkout type
    let delivery;
    let dateParams = {};

    if (checkoutType === "direct_checkout") {
      // Direct checkout = Library use = PICKUP + Same day loan
      delivery = { method: "PICKUP" };
      dateParams = {
        start_date: today,
        end_date: today, // Same day for library use
        request_start_date: today,
        request_expire_date: today,
      };
    } else if (checkoutType === "fulfill_specific_request") {
      // For specific requests, use existing delivery preferences from request
      delivery = { method: "PICKUP" };
      // Dates will be handled by the backend from the original request
    } else if (checkoutType === "fulfill_alternate_request") {
      // For alternate requests, use pickup but allow normal loan period
      delivery = { method: "PICKUP" };
    }

    // Call the checkout function with enhanced data INCLUDING borrower_info
    const finalPayload = {
      ...checkoutData,
      delivery,
      ...dateParams,
      borrower_info: borrowerInfo,
    };

    checkoutItem(documentPid, itemPid, patronPid, finalPayload);

    return true;
  };

  handleKeyPress = async (event) => {
    if (event.key === "Enter") {
      this.setState({ checkOutOnEnter: true });
    }
  };

  onSearchChange = (value) => {
    this.setState({
      checkOutOnEnter: false,
      errorMessage: null,
    });
  };

  onResults = (results, autoCheckOut = false) => {
    if (!autoCheckOut) {
      const { checkOutOnEnter } = this.state;
      autoCheckOut = checkOutOnEnter;
    }
    if (autoCheckOut && this.hasCheckOutItem() && results.length === 1) {
      this.checkoutItem(results);
    }
  };

  patronSearch = async (query) => {
    const { item } = this.props;
    const documentPid = item.metadata.document_pid;
    const itemPid = item.metadata.pid;

    try {
      const patronsResponse = await patronApi.list(
        patronApi
          .query()
          .withPid(query)
          .withEmail(query)
          .withName(query, true)
          .withCustomField(query)
          .qs(),
        false
      );

      const itemLoanRequestsResponse = await loanApi.list(
        loanApi.query().withItemPid(itemPid).withState(["PENDING"]).qs()
      );

      const documentLoanRequestsResponse = await loanApi.list(
        loanApi.query().withDocPid(documentPid).withState(["PENDING"]).qs()
      );

      const patronPidsWithItemRequests = itemLoanRequestsResponse.data.hits.map(
        (loan) => loan.metadata.patron_pid
      );

      const patronPidsWithDocumentRequests =
        documentLoanRequestsResponse.data.hits.map(
          (loan) => loan.metadata.patron_pid
        );

      const patronsWithEnhancedInfo = await Promise.all(
        patronsResponse.data.hits.map(async (patron) => {
          try {
            const patronId =
              patron.metadata.pid || patron.metadata.id?.toString();

            const hasRequestForThisItem =
              patronPidsWithItemRequests.includes(patronId);
            const hasRequestForDocument =
              patronPidsWithDocumentRequests.includes(patronId);

            const overdueLoansResponse = await loanApi.list(
              loanApi
                .query()
                .withPatronPid(patronId)
                .overdue()
                .withState(invenioConfig.CIRCULATION.loanActiveStates)
                .qs()
            );

            const hasOverdueLoans = overdueLoansResponse.data.hits.length > 0;

            let patronTags = [];
            let requestPriority = "normal";

            if (hasRequestForThisItem) {
              patronTags.push({
                type: "request_this_copy",
                label: "Has Request for This Copy",
                color: "green",
                icon: "check circle",
              });
              requestPriority = "high";
            } else if (hasRequestForDocument) {
              patronTags.push({
                type: "request_other_copy",
                label: "Has Request for Other Copy",
                color: "blue",
                icon: "info circle",
              });
              requestPriority = "medium";
            } else {
              patronTags.push({
                type: "no_request",
                label: "No Prior Request",
                color: "grey",
                icon: "circle outline",
              });
              requestPriority = "normal";
            }

            if (hasOverdueLoans) {
              patronTags.push({
                type: "overdue",
                label: "Has Overdue Items",
                color: "red",
                icon: "warning circle",
              });
            }

            return {
              ...patron,
              metadata: {
                ...patron.metadata,
                hasOverdueLoans,
                canCheckout: !hasOverdueLoans,
                hasRequest: hasRequestForDocument,
                hasRequestForThisItem,
                hasRequestForOtherCopy:
                  hasRequestForDocument && !hasRequestForThisItem,
                requestPriority,
                patronTags,
              },
            };
          } catch (error) {
            return {
              ...patron,
              metadata: {
                ...patron.metadata,
                hasOverdueLoans: true,
                canCheckout: false,
                hasRequest: false,
                hasRequestForThisItem: false,
                hasRequestForOtherCopy: false,
                requestPriority: "normal",
                patronTags: [
                  {
                    type: "error",
                    label: "Unable to Check Status",
                    color: "orange",
                    icon: "exclamation triangle",
                  },
                ],
              },
            };
          }
        })
      );

      const sortedPatrons = patronsWithEnhancedInfo.sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, normal: 1 };
        return (
          priorityOrder[b.metadata.requestPriority] -
          priorityOrder[a.metadata.requestPriority]
        );
      });

      return {
        ...patronsResponse,
        data: {
          ...patronsResponse.data,
          hits: sortedPatrons,
          total: sortedPatrons.length,
        },
      };
    } catch (error) {
      return { data: { hits: [], total: 0 } };
    }
  };

  render() {
    const { item, deleteItem, offset } = this.props;
    const { errorMessage } = this.state;
    const hasCheckOutItem = this.hasCheckOutItem();

    if (typeof document !== "undefined") {
      const styleId = "patron-text-fix";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          .ui.modal .ui.list .item .content *,
          .ui.modal .ui.selection.dropdown .item {
            color: white !important;
          }
        `;
        document.head.appendChild(style);
      }
    }

    return (
      <div className="bo-action-menu">
        <EditButton
          fluid
          to={BackOfficeRoutes.itemEditFor(item.metadata.pid)}
          text="Edit Physical Copy"
          style={{ textAlign: "right", marginRight: "25px" }}
        />
        <div style={{ textAlign: "right" }}>
          <DeleteRecordModal
            trigger={this.deleteDocButton}
            deleteHeader={`Are you sure you want to delete the physical copy
              record with ID ${item.pid}?`}
            onDelete={() => deleteItem(item.pid)}
            refProps={this.createRefProps(item.pid)}
          />
        </div>
        <Divider horizontal> Circulation </Divider>
        <ESSelectorModal
          modalOpened={hasCheckOutItem}
          autoSelect={hasCheckOutItem}
          minCharacters={hasCheckOutItem ? 1 : 3}
          onResults={this.onResults}
          handleKeyPress={this.handleKeyPress}
          onSearchChange={this.onSearchChange}
          trigger={this.checkoutItemButton()}
          query={this.patronSearch}
          serializer={serializePatron}
          title={`You are about to checkout the physical copy of ${item.metadata.document.title}.`}
          content={
            <>
              {errorMessage && (
                <Message negative style={{ marginBottom: "1rem" }}>
                  {errorMessage}
                </Message>
              )}
              Insert borrower's name or email to create a loan:
            </>
          }
          selectionInfoText="The loan will be created for the following patron:"
          emptySelectionInfoText="No patron selected yet"
          onSave={this.checkoutItem}
          saveButtonContent="Checkout physical copy"
          focus
        />
        <Divider horizontal> Navigation </Divider>
        <ScrollingMenu offset={offset}>
          <ScrollingMenuItem label="Circulation" elementId="circulation" />
          <ScrollingMenuItem label="Metadata" elementId="metadata" />
          <ScrollingMenuItem label="Loans request" elementId="loans-request" />
          <ScrollingMenuItem label="Loans history" elementId="loans-history" />
        </ScrollingMenu>
      </div>
    );
  }
}

ItemActionMenu.propTypes = {
  item: PropTypes.object.isRequired,
  offset: PropTypes.number,
  deleteItem: PropTypes.func.isRequired,
  checkoutItem: PropTypes.func.isRequired,
  checkOutitemList: PropTypes.arrayOf(PropTypes.object),
};

ItemActionMenu.defaultProps = {
  offset: 0,
  checkOutitemList: [],
};
